import axios from 'axios';
import { createQuery } from 'react-query-kit';

import { getInjector, getRootScope } from '@/services/angularjs-bridge';
import { logger } from '@/services/logger';
import { restUrl } from '@/services/utils';

type TaskList = {
    id: number;
    title?: string;
};

type TaskUser = {
    userId: string;
    displayName?: string;
};

type TaskAttachment = {
    url: string;
    name: string;
    contentType?: string;
};

type NewTask = {
    title: string;
    taskListId?: number;
    dueDate?: string;
    assignee?: string;
    notes?: string;
    tags?: string[];
    items?: string[];
    selectionId?: string;
};

type SavedTask = NewTask & {
    id: number;
    author?: string;
};

const useTaskLists = createQuery({
    queryKey: ['taskLists'],
    fetcher: async (): Promise<TaskList[]> => {
        const response = await axios.get(restUrl('taskLists'));
        return Array.isArray(response.data) ? response.data : [];
    },
});

const readAngularUsers = (): TaskUser[] | undefined => {
    try {
        const users = getRootScope(getInjector()).users;
        return Array.isArray(users) && users.length > 0 ? users as TaskUser[] : undefined;
    } catch {
        return undefined;
    }
};

/** `$rootScope.users` once the shell has loaded them, otherwise GET `users`. */
const useTaskUsers = createQuery({
    queryKey: ['taskUsers'],
    fetcher: async (): Promise<TaskUser[]> => {
        const angularUsers = readAngularUsers();
        if (angularUsers) {
            return angularUsers;
        }
        const response = await axios.get(restUrl('users'));
        return Array.isArray(response.data) ? response.data : [];
    },
});

/** POST `assets`, mapped like `CreateTaskController#uploadFileForItem`. */
const uploadTaskAttachment = async (file: File): Promise<TaskAttachment[]> => {
    const body = new FormData();
    body.append('file', file);
    const response = await axios.post(restUrl('assets'), body);
    const assets: Array<{ url: string; path: string; contentType?: string }> = Array.isArray(response.data)
        ? response.data
        : [];
    return assets.map((asset) => ({
        url: asset.url,
        name: asset.path.replace(/^\//, ''),
        contentType: asset.contentType,
    }));
};

/** The task was saved, but storing its attachment comment failed. */
class TaskAttachmentCommentError extends Error {
    readonly saved: SavedTask;

    constructor(saved: SavedTask) {
        super('Task attachment comment failed');
        this.name = 'TaskAttachmentCommentError';
        this.saved = saved;
    }
}

/**
 * POST `taskLists/tasks`, then the attachment as a first comment on the
 * new task, as `CreateTaskController#saveTask` does. A failed comment still
 * rejects with the saved task so the caller does not create it again.
 */
const createTask = async (task: NewTask, attachments: TaskAttachment[]): Promise<SavedTask> => {
    const response = await axios.post<SavedTask>(restUrl('taskLists/tasks'), task);
    const saved = response.data;
    if (attachments.length === 0) {
        return saved;
    }
    try {
        await axios.post(restUrl(`taskLists/tasks/${saved.id}/comments`), {
            assets: attachments,
            author: saved.author,
            text: '',
            taskId: saved.id,
        });
    } catch (error) {
        logger.error('Storing the task attachment failed', error);
        throw new TaskAttachmentCommentError(saved);
    }
    return saved;
};

export type { NewTask, SavedTask, TaskAttachment, TaskList, TaskUser };
export { createTask, TaskAttachmentCommentError, uploadTaskAttachment, useTaskLists, useTaskUsers };
